// W36: thin wrapper over the generated settings client.
//
// Types come from `./generated/settings` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate settings`); the request path
// stays on the house core so the envelope, Idempotency-Key, token refresh and
// ApiError mapping behave exactly as before.
//
// W46 gave the service the write surface this module has always advertised:
// `GET /v1/settings`, `PUT /v1/settings/{key}` and `DELETE /v1/settings/{key}`
// are mapped and authorised by each key's `editable_by` (_docs/10
// § Permissions). The two reads (`GET /v1/settings/{key}` and
// `/v1/settings/keys`) stay `.AllowAnonymous()` so the login screen can render
// the brand before there is a token.

import { http } from './_core/request.js';
import type { Envelope, EnvelopeWithMeta, Meta } from './_core/envelope.js';
import type * as gen from './generated/settings/index.js';

// ── Types (generated) ──────────────────────────────────────────────────────

/**
 * The scope codes `services/settings` understands.
 *
 * Hand-written: the service models scopes as `short` constants
 * (`services/settings/Domain/SettingEntry.cs` → `SettingScope`), not as an
 * OpenAPI enum, so there is nothing to generate from. These are exactly the
 * codes `SettingScope.Parse` accepts, and they resolve to the ids that come
 * back as `SettingResult['meta']['scope_id']`:
 *
 *   global 1 · country 2 · org 3 · sub_account 4 · phone 5 · user 6
 *
 * ⚠ DRIFT; this union used to read `'global' | 'org' | 'sub'`. `'sub'` is not
 * a code `SettingScope.Parse` knows: it throws, which is a 400 `UNKNOWN_SCOPE`
 * on the feature-flag override route (the only place a code is parsed today).
 * `country` / `phone` / `user` were missing. No call site in `apps/` or `sdk/`
 * references the type, so it is corrected here rather than pinned.
 */
export type SettingScope = 'global' | 'country' | 'org' | 'sub_account' | 'phone' | 'user';

/**
 * `GET /v1/settings/{key}`: one resolved setting plus the scope the cascade
 * actually matched on.
 *
 * `meta` is the generated `SettingValueMeta`; only `data` stays hand-written,
 * because the generated `SettingValueResponse` types it `unknown` while this
 * module's public contract is the caller-supplied `T`.
 *
 * Two things to know about the payload:
 * - ⚠ DRIFT: `meta.scope_id` used to be declared optional
 *   `number | string | null`. The handler always emits the matched scope as a
 *   non-null `short`, so the generated required `number` is the truth. Nothing
 *   read the field, so it is corrected rather than pinned.
 * - ⚠ DRIFT: the service sets `DefaultIgnoreCondition = WhenWritingNull`
 *   (`services/settings/Program.cs`), so a null member is *absent* from the
 *   JSON rather than present-and-null: `meta.scope_target` disappears on a
 *   global hit, and `data` disappears entirely when the stored `jsonb` value is
 *   JSON `null`. Both keep their declared types`scope_target`'s `| null`
 *   already forces the same guard `undefined` needs, and `data` must stay `T`
 *   for the generic to mean anything.
 *
 * The handler also reads an optional `?country=` query for the cascade; this
 * client has never sent it, and does not start now.
 */
export type SettingResult<T = unknown> = Omit<gen.SettingValueResponse, 'data'> & {
  data: T;
};

// ── Operations ─────────────────────────────────────────────────────────────

export function getSetting<T = unknown>(key: string): Promise<SettingResult<T>> {
  return http.get<SettingResult<T>>(`/v1/settings/${encodeURIComponent(key)}`);
}

/**
 * `GET /v1/settings/keys`: every globally-scoped key, sorted.
 *
 * The house `Envelope` stays the wrapper: the generated `ListEnvelopeOfstring`
 * declares `meta: { count, total, limit }`, of which the handler only ever sets
 * `count`, and `Envelope['meta']` already carries that through its index
 * signature.
 */
export function listSettingKeys(): Promise<Envelope<string[]>> {
  return http.get<Envelope<string[]>>('/v1/settings/keys', { auth: false });
}

// ── Write surface (W46) ────────────────────────────────────────────────────
// Hand-typed until the generator is next run against the reconciled service.

/**
 * One stored `setting_entries` row: the row, not the resolved cascade:
 * `scope`/`scope_id` say which layer the row LIVES at, where
 * {@link SettingResult}'s `meta` says which layer ANSWERED a read.
 *
 * A row flagged `is_sensitive` has its `value` replaced by a fixed mask
 * (`••••••••`) for anyone but a platform admin.
 */
export interface Setting<T = unknown> {
  key: string;
  scope: SettingScope;
  scope_id: number;
  /** The org/user/phone id or country ISO the row is scoped to; absent at global scope. */
  scope_target?: string | null;
  value: T;
  value_type: string;
  is_sensitive: boolean;
  description?: string | null;
  /**
   * Roles allowed to write this key (_docs/10 § Permissions). An empty array
   * means the key declares nothing, which the service reads as platform-admin
   * only; the write surface fails closed.
   */
  editable_by: string[];
  updated_at: string;
  updated_by?: string | null;
}

export interface SetSettingOpts {
  /** Defaults to `global` for a platform token, `org` for a tenant one. */
  scope?: SettingScope;
  /** Defaults to the caller's own organisation (or user, at `user` scope). */
  scope_target?: string;
  value_type?: string;
  is_sensitive?: boolean;
  description?: string;
  /** Re-declare who may edit the key. Honoured for platform admins only. */
  editable_by?: string[];
}

export interface ListSettingsOpts {
  scope?: SettingScope;
  scope_target?: string;
  /** Only keys starting with this string. */
  prefix?: string;
  limit?: number;
  offset?: number;
}

/**
 * Write one key at one scope.
 *
 * Fails with 403 `KEY_NOT_EDITABLE` when the key's `editable_by` does not
 * include the caller's role, 403 `SCOPE_FORBIDDEN` when a tenant token aims at
 * a scope that is not its own, and 404 `UNKNOWN_KEY` when a tenant token
 * writes a key no scope declares (only a platform admin can create one).
 */
export function setSetting<T = unknown>(
  key: string,
  value: T,
  opts?: SetSettingOpts
): Promise<Envelope<Setting<T>>> {
  return http.put<Envelope<Setting<T>>>(`/v1/settings/${encodeURIComponent(key)}`, {
    body: { value, ...opts }
  });
}

/**
 * The stored rows. A platform token sees every scope; a tenant token sees the
 * layers that apply to it: global, country, its own org and its own user rows
 *: and never another tenant's.
 */
export function listSettings(opts?: ListSettingsOpts): Promise<Envelope<Setting[]>> {
  const query: Record<string, string | number | undefined> = {};
  if (opts?.scope !== undefined) query.scope = opts.scope;
  if (opts?.scope_target !== undefined) query.scope_target = opts.scope_target;
  if (opts?.prefix !== undefined) query.prefix = opts.prefix;
  if (opts?.limit !== undefined) query.limit = opts.limit;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  return http.get<Envelope<Setting[]>>('/v1/settings', { query });
}

/**
 * Remove the row at ONE scope so the cascade falls back to the next layer up.
 * Idempotent: deleting a row that is not there is still a 204.
 */
export function deleteSetting(
  key: string,
  opts?: { scope?: SettingScope; scope_target?: string }
): Promise<void> {
  const query: Record<string, string | undefined> = {};
  if (opts?.scope !== undefined) query.scope = opts.scope;
  if (opts?.scope_target !== undefined) query.scope_target = opts.scope_target;
  return http.delete<void>(`/v1/settings/${encodeURIComponent(key)}`, { query });
}

// ── Ops write paths (7 September 2026) ─────────────────────────────────────
//
// `/v1/admin/ops/*` is served by the settings service, so its typed methods
// live here beside the rest of that service's surface. The read half (queues,
// service health) has always been reachable through `admin.listQueues()` and
// `admin.listBackups()`; those stay where they are and keep working. What is
// new is the write path, plus the audit trail it leaves and the snapshot
// history the backups route now actually returns.
//
// Hand-written rather than generated: `src/generated/settings` is one
// regeneration behind while parallel service work settles. Run
// `pnpm --filter @repletoryreach/sdk-api generate settings` afterwards and
// these shapes can come from there.

/** Where one message went, and on what evidence. */
export interface RequeueMove {
  exchange: string;
  routing_key: string;
  /**
   * How the destination was decided.
   *
   * - `target`: the caller named a queue.
   * - `x_death`: the broker's own dead-letter header.
   * - `fault_input_address`: MassTransit's `MT-Fault-InputAddress`, which is
   *   the case for every `*_error` queue this platform has, because MassTransit
   *   publishes faults to a `<queue>_error` exchange rather than using a DLX
   *   and therefore writes no `x-death` at all.
   * - `queue_name`: the queue's own name with `_error` / `_skipped` trimmed,
   *   the last honest guess.
   */
  source: 'target' | 'x_death' | 'fault_input_address' | 'queue_name';
  outcome: 'requeued' | 'restored' | 'failed';
  message_id?: string | null;
  /** Why the delivery failed, on a `restored` or `failed` move. */
  error?: string | null;
}

export interface RequeueResult {
  queue: string;
  /** Null when each message was routed by its own fault address. */
  target?: string | null;
  requeued: number;
  /**
   * Taken off the dead-letter queue, undeliverable, and put straight back.
   * Should be zero. When it is not, it is the receipt that nothing was lost.
   */
  restored: number;
  /** Neither delivered nor put back. Should always be zero; investigate if not. */
  failed: number;
  /**
   * Messages left on the queue, as the broker last reported it.
   *
   * ⚠ Absent when the broker did not say. RabbitMQ's management API serves this
   * from a statistics database it samples on an interval, so the figure is
   * routinely missing or stale for a few seconds after a move. Render "not
   * reported" rather than a confident zero.
   */
  remaining?: number | null;
  requeued_at: string;
  actor: string;
  /** The `ops_audit_events` row this move wrote. */
  audit_id: string;
  moves: RequeueMove[];
  /**
   * Why the move stopped before it had attempted `count` messages, when it did.
   * A move is given a wall-clock budget so a slow broker returns a truthful
   * partial result rather than being cut off by a proxy after the messages have
   * already moved; a caller that goes away lands here too.
   *
   * Absent on a run that finished, and never a reason to distrust the counts
   * beside it: those messages really did move. Show it, because it is the
   * operator's cue to run the same move again.
   */
  stopped?: string | null;
}

export interface RequeueOptions {
  /** 1…500, default 1. There is deliberately no "all". */
  count?: number;
  /** Destination queue. Omit to route each message by its own fault address. */
  target?: string | null;
  /** Required. Stored on the audit row. */
  reason: string;
}

/**
 * Move dead-lettered messages back onto the queue that should have handled
 * them. Platform scope only.
 *
 * A message in a dead-letter queue is business that did not happen: a wallet
 * not credited, an inbound message not filed. This is how an operator recovers
 * it. The service takes messages one at a time, checks the destination exists
 * before it removes anything, and puts back anything it cannot deliver, so a
 * failed recovery costs nothing. A message that is delivered and faults again
 * returns to the same dead-letter queue through the consumer's own error
 * pipeline, which is the intended behaviour: recovery is retried, never
 * assumed.
 *
 * Fails with 400 `NO_SUCH_QUEUE`, `NO_SUCH_TARGET`, `TARGET_IS_SOURCE`,
 * `INVALID_COUNT`, `REASON_REQUIRED` or `EMPTY_QUEUE`; 502
 * `BROKER_UNREACHABLE` when the broker cannot be reached (nothing was moved);
 * and 503 `AUDIT_UNAVAILABLE` when the audit row could not be written, in which
 * case the move is refused rather than performed unrecorded.
 */
export function requeueQueue(queue: string, opts: RequeueOptions): Promise<Envelope<RequeueResult>> {
  return http.post<Envelope<RequeueResult>>(
    `/v1/admin/ops/queues/${encodeURIComponent(queue)}/requeue`,
    { body: { count: opts.count ?? 1, target: opts.target ?? null, reason: opts.reason } }
  );
}

/** One row of the ops audit trail. */
export interface OpsAuditEvent {
  id: string;
  /** Code from `lookup_ops_action_types`, e.g. `queue.requeue`. */
  action: string;
  /** Never blank: a user's `sub`, or a named `system:<service>` subject. */
  actor: string;
  actor_email?: string | null;
  source: string;
  destination?: string | null;
  requested_count: number;
  affected_count: number;
  restored_count: number;
  failed_count: number;
  /**
   * `pending` while the action is in flight, then `succeeded`, `partial`,
   * `failed` or `refused`. A row left at `pending` means the service died mid
   * action, which is itself worth seeing.
   */
  outcome: 'pending' | 'succeeded' | 'partial' | 'failed' | 'refused';
  reason: string;
  detail?: string | null;
  correlation_id?: string | null;
  started_at: string;
  completed_at?: string | null;
}

export interface OpsAuditOptions {
  /** Narrow to one queue. */
  source?: string;
  /** 1…200, default 50. */
  limit?: number;
  offset?: number;
}

export interface OpsAuditEnvelope extends Envelope<OpsAuditEvent[]> {
  meta?: { count: number; total?: number | null; limit?: number | null };
}

/** Newest-first history of every ops write path. Platform scope only. */
export function opsAudit(opts: OpsAuditOptions = {}): Promise<OpsAuditEnvelope> {
  const query: Record<string, string | number> = {};
  if (opts.source) query.source = opts.source;
  if (opts.limit !== undefined) query.limit = opts.limit;
  if (opts.offset !== undefined) query.offset = opts.offset;
  return http.get<OpsAuditEnvelope>('/v1/admin/ops/audit', Object.keys(query).length ? { query } : undefined);
}

/** One database inside a snapshot, as `manifest.json` records it. */
export interface BackupDatabase {
  database: string;
  file?: string | null;
  bytes: number;
  sha256?: string | null;
  live_rows?: number | null;
  /** Whether the dump is actually in the store. Absent when the store could not be listed. */
  present?: boolean | null;
  /** Bytes the store holds. Larger than `bytes` on an encrypted copy, which is expected. */
  stored_bytes?: number | null;
}

/** One snapshot written by `scripts/dr/backup.sh`. */
export interface BackupSnapshot {
  /** The directory or key prefix, e.g. `20260907T014812Z`. */
  id: string;
  taken_at?: string | null;
  host?: string | null;
  container?: string | null;
  location: string;
  encryption: string;
  database_count: number;
  total_bytes: number;
  total_live_rows?: number | null;
  databases: BackupDatabase[];
  /**
   * Every dump the manifest names is present, and on an unencrypted copy holds
   * the recorded number of bytes. Absent when the store could not be listed.
   *
   * ⚠ This is a completeness check, not a re-hash: verifying the recorded
   * sha256 would mean downloading every dump on every page load. `meta.
   * verification` says which was done, and each database's `sha256` is
   * projected so one can be checked by hand.
   */
  verified?: boolean | null;
  /** Manifest keys the service does not model, carried through verbatim. */
  extra?: Record<string, unknown> | null;
}

export interface BackupListMeta extends Meta {
  count: number;
  source?: 's3' | 'local' | null;
  location?: string | null;
  latest_at?: string | null;
  oldest_at?: string | null;
  /** Hours since the newest snapshot. Absent when there is none: "0 hours old" and "no backup" must never render alike. */
  age_hours?: number | null;
  verification: 'size' | 'none';
  command: string;
  restore: string;
  /** `backup_store_unreachable` or `backup_store_not_configured`. Absent when the store read cleanly. */
  error?: string | null;
  /** Why the store could not be read. Only alongside `backup_store_unreachable`. */
  message?: string | null;
  /**
   * One sentence describing the state of the store, always present and always
   * true: how to configure one when none is, why it could not be read, that it
   * is empty, or what the newest snapshot covers. Branch on `error` to decide
   * how to render it, not on whether it exists.
   */
  notice: string;
}

export type BackupListEnvelope = EnvelopeWithMeta<BackupSnapshot[], BackupListMeta>;

/**
 * Snapshot history, newest first. Platform scope only.
 *
 * `scripts/dr/backup.sh` writes a `manifest.json` beside every snapshot and
 * uploads it last, so a prefix that has one is a complete backup and one
 * without it is a half-finished upload. That is the index this reads.
 *
 * The store is resolved in order: `BACKUP_S3_BUCKET` (or `Backups:S3Bucket`),
 * then the `ops.backups.*` settings keys, then the `aws_s3` integration, then
 * the local snapshot directory. When none is configured the call still answers
 * 200 with an empty list and `meta.error = "backup_store_not_configured"`,
 * which is the same shape the queues route uses for a broker it cannot reach.
 */
export function listBackupSnapshots(opts: { limit?: number } = {}): Promise<BackupListEnvelope> {
  return http.get<BackupListEnvelope>(
    '/v1/admin/ops/backups',
    opts.limit === undefined ? undefined : { query: { limit: opts.limit } }
  );
}

// ── Integration field choices (Settings → Integrations) ───────────────────
//
// A provider's `field_schema` can declare a field as `select_remote`: its
// choices are not seeded but worked out by the settings service from the
// DRAFT values on the form (the region and keys just typed, saved or not).
// The Bedrock model ids and the AWS regions are the first such fields: in
// Cape Town the Anthropic models are only callable through their inference
// profiles, and an id typed from memory saved, tested green and answered
// nothing. `admin.IntegrationFieldSchema` predates this kind; the types
// below are the superset the console's integration drawer reads through.

/**
 * `select_remote` joins the seeded kinds: a searchable pick-list whose
 * choices come from {@link integrationFieldOptions}. `enum` stays what it
 * was, a fixed list in `options`.
 */
export type IntegrationFieldKind = 'string' | 'password' | 'url' | 'enum' | 'select_remote';

export interface IntegrationFieldSchema {
  key: string;
  label: string;
  kind: IntegrationFieldKind;
  required?: boolean;
  /** Rendered under the control (CLAUDE.md § Form field hints). */
  description?: string;
  /** `enum` only: the fixed choices. */
  options?: string[];
  /** `select_remote` only: which list the service builds (`aws_regions`, `bedrock_text_models`, `bedrock_embedding_models`). */
  source?: string;
  /**
   * `select_remote` only: fields that must be filled in before the list can
   * be asked for. Empty means the list loads as soon as the drawer opens.
   */
  depends_on?: string[];
  /**
   * `select_remote` only: fields the source reads when they are set, so a
   * change reloads the list, without blank blocking it (the embedding model
   * list reads `embedding_region` and falls back to `region`).
   */
  also_reads?: string[];
}

/**
 * One choice. `group` is the heading it sits under, in the order the service
 * lists them (Bedrock puts "Inference profiles (recommended in this region)"
 * above "Models"). A `disabled` choice is shown but not selectable, with
 * `hint` saying why: a bare Anthropic id that is only callable through its
 * inference profile is offered that way rather than hidden, so an operator
 * learns why the id they remember does not work.
 */
export interface IntegrationFieldOption {
  value: string;
  label: string;
  group?: string | null;
  hint?: string | null;
  disabled?: boolean | null;
}

export interface IntegrationFieldOptions {
  options: IntegrationFieldOption[];
  /** One sentence about where the list came from: "12 of 29 regions have Bedrock", "no embedding model in af-south-1". */
  note?: string | null;
}

export interface IntegrationFieldOptionsRequest {
  /**
   * The form's draft values, saved or not. A password field holding the mask
   * (`••••••••`) stands for the stored secret and is substituted server-side.
   */
  config: Record<string, unknown>;
  /** The org-scoped row's stored secrets are used for the mask when set. */
  org_id?: string;
}

/**
 * `POST /v1/admin/integrations/{provider_code}/options/{field}`: the choices
 * for a `select_remote` field, from the draft. Platform scope only.
 *
 * Errors are typed, never a 500: 400 `MISSING_DEPENDENCIES` (with
 * `details.fields`) when a `depends_on` field is blank, 400 `FIELD_NOT_REMOTE`
 * for a hand-typed field, 404 `FIELD_NOT_FOUND` / `PROVIDER_NOT_FOUND`, and
 * 422 `SOURCE_REFUSED` / `SOURCE_TIMEOUT` / `SOURCE_UNREACHABLE` /
 * `SOURCE_FAILED` when the upstream would not answer, with a message that
 * names the region and never repeats a credential. The service caches a
 * Bedrock catalogue for a minute per region and key, and the region probes
 * for a day.
 */
export function integrationFieldOptions(
  providerCode: string,
  fieldKey: string,
  req: IntegrationFieldOptionsRequest
): Promise<Envelope<IntegrationFieldOptions>> {
  return http.post<Envelope<IntegrationFieldOptions>>(
    `/v1/admin/integrations/${encodeURIComponent(providerCode)}/options/${encodeURIComponent(fieldKey)}`,
    { body: req }
  );
}
